<template>
    <DxFileManager
        ref="fileManager"
        :toolbar="toolbar"
        :contextMenu="contextMenu"
        :permissions="permissions"
        :file-system-provider="fileSystemProvider"
        :height="height"
        :on-current-directory-changed="onDirectoryChanged"
        :on-current-directory-creating="onDirectoryCreating"
        :on-item-deleting="onItemDeleting"
        :on-toolbar-item-click="onToolbarItemClick"
        :on-item-renamed="onItemRenamed"
        :on-context-menu-item-click="onContextMenuItemClick"
        :on-item-moving="onItemMoving"
        :on-selected-file-opened="onSelectedFileOpened"
        :customize-thumbnail="customizeIcon"
        :customize-detail-columns="customizeColumn"
    >
    </DxFileManager>
</template>

<script>
import 'devextreme/dist/css/dx.light.css';
import {DxFileManager} from 'devextreme-vue/file-manager';
import ObjectFileSystemProvider from 'devextreme/file_management/object_provider';
import TreeView from 'devextreme/ui/tree_view';

import {locale, loadMessages} from 'devextreme/localization';
import trMessages from '@/assets/plugin/dev-express/locales/tr.json';

loadMessages(trMessages);

export default {
    name: "index",
    emit: [
        "directoryChanged",
        "directoryCreating",
        "directoryDeleting",
        "directoryRenamed",
        "directoryMoving",
        "fileDeleting",
        "fileRenamed",
        "fileMoving",
        "fileOpened",
        "toolbarItemClick",
        "contextMenuItemClick",
    ], //extraMethods => fileUploadEvent
    props: {
        toolbar: {
            type: Object, default: () => {
                return {
                    items: ['showNavPane', 'create', 'upload', 'switchView', {name: 'separator', location: 'after'}, 'refresh'],
                    fileSelectionItems: ['download', 'separator', 'move', 'copy', 'rename', 'separator', 'delete', 'clearSelection', {name: 'separator', location: 'after'}, 'refresh']
                }
            }
        },
        contextMenu: {
            type: Object, default: () => {
                return {
                    items: ['create', 'upload', 'rename', 'move', 'copy', 'delete', 'refresh', 'download'],
                }
            }
        },
        permissions: {
            type: Object, default: () => {
                return {create: true, upload: true, move: true, delete: true, rename: true}
            }
        },
        customUpload: {type: Boolean, default: true},
        excelExport: {type: Boolean, default: false},
        items: {type: Array, required: true},
        height: {type: Number, default: 450},
        localization: {type: String, default: "tr"},
        nameExpr: {type: String, default: "title"},
        isDirectoryExpr: {type: String, default: "is_directory"},
        dateModifiedExpr: {type: String, default: "updated_at"},
        keyExpr: {type: String, default: "key"},
        itemsExpr: {type: String, default: "children"},
        customizeIcon: {type: Function, default: undefined},
        customizeColumn: {type: Function, default: undefined}
    },
    components: {
        DxFileManager,
    },
    data() {
        return {
            locale: locale,
            lastCurrentPath: '',
            pendingNavigatePath: null,
        }
    },
    computed: {
        fileManager() {
            return this.$refs.fileManager.instance;
        },
        fileSystemProvider() {
            return new ObjectFileSystemProvider({
                data: this.items,
                nameExpr: this.nameExpr,
                itemsExpr: this.itemsExpr,
                isDirectoryExpr: this.isDirectoryExpr,
                dateModifiedExpr: this.dateModifiedExpr
            })
        }
    },
    created() {
        if (this.customUpload) {
            let uploadIndex = this.toolbar.items.indexOf('upload');
            this.toolbar.items[uploadIndex] = {
                name: 'fileUpload',
                options: {
                    text: this.$t('common.fileUpload'),
                    icon: 'upload'
                }
            };

            let contextUploadIndex = this.contextMenu.items.indexOf('upload');
            this.contextMenu.items[contextUploadIndex] = {
                name: 'fileUpload',
                text: this.$t('common.fileUpload'),
                icon: 'upload'
            }
        }

        if (this.excelExport) {
            this.toolbar.items.push({
                name: 'excelExport',
                options: {
                    text: this.$t('common.excelExport'),
                    icon: 'exportxlsx'
                },
                location: 'after'
            });

            this.toolbar.fileSelectionItems.push({
                name: 'excelExport',
                options: {
                    text: this.$t('common.excelExport'),
                    icon: 'exportxlsx'
                },
                location: 'after'
            });

            this.contextMenu.items.push({
                name: 'excelExport',
                text: this.$t('common.excelExport'),
                icon: 'exportxlsx'
            });
        }

        if (this.localization) {
            this.setLocale(this.localization)
        }
    },
    methods: {
        setLocale(locale) {
            this.locale(locale);
        },
        onDirectoryChanged(e) {
            this.lastCurrentPath = (e.directory && e.directory.path) || '';
            this.$emit("directoryChanged", e);
        },
        onDirectoryCreating(e) {
            this.$emit("directoryCreated", e);
        },
        onItemDeleting(e) {
            this.$emit((e.item.isDirectory ? "directoryDeleting" : "fileDeleting"), e, e.item.dataItem);
        },
        onToolbarItemClick(e) {
            this.$emit("toolbarItemClick", e);
            this.commonCustomEvents(e);
        },
        onItemRenamed(e) {
            this.$emit((e.sourceItem.isDirectory ? "directoryRenamed" : "fileRenamed"), e.itemName, e.sourceItem.dataItem);
        },
        onContextMenuItemClick(e) {
            this.$emit("contextMenuItemClick", e);
            this.commonCustomEvents(e);
        },
        commonCustomEvents(e) {
            let itemData = e.itemData.name ?? e.itemData;

            if (itemData) {
                let targetDirectory = (e.fileSystemItem && e.fileSystemItem.isDirectory)
                    ? e.fileSystemItem
                    : this.fileManager.getCurrentDirectory();

                this.$emit((this.sprintf('%sEvent', [itemData])), e, targetDirectory, (this.fileManager.getSelectedItems() || []));
            }
        },
        onItemMoving(e) {
            let destinationDirectory = e.destinationDirectory;
            let item = e.item;
            this.$emit((item.isDirectory ? "directoryMoving" : "fileMoving"), e, item.dataItem, destinationDirectory.dataItem);
        },
        onSelectedFileOpened(e) {
            this.$emit("fileOpened", e.file);
        },
        navigateToPathAfterReload(path) {
            this.pendingNavigatePath = path || '';
        },
        getRootEl() {
            return (this.$refs.fileManager && this.$refs.fileManager.$el) || null;
        },
        getDirsScrollEl() {
            let root = this.getRootEl();
            return root ? root.querySelector('.dx-filemanager-dirs-panel .dx-scrollable-container') : null;
        },
        getFilesScrollEl() {
            let root = this.getRootEl();
            if (!root) return null;
            return root.querySelector('.dx-filemanager-files-view .dx-datagrid-rowsview .dx-scrollable-container')
                || root.querySelector('.dx-filemanager-files-view .dx-scrollable-container');
        },
        getDirsTreeView() {
            let root = this.getRootEl();
            if (!root) return null;
            let el = root.querySelector('.dx-filemanager-dirs-tree');
            return el ? TreeView.getInstance(el) : null;
        },
        captureUiState() {
            let root = this.getRootEl();
            if (!root) return null;

            let expandedKeys = [];
            root.querySelectorAll('.dx-filemanager-dirs-tree .dx-treeview-node[aria-expanded="true"]').forEach((node) => {
                let raw = node.getAttribute('data-item-id');
                if (raw) {
                    try {
                        expandedKeys.push(decodeURI(raw));
                    } catch (e) {
                        expandedKeys.push(raw);
                    }
                }
            });

            let left = this.getDirsScrollEl();
            let right = this.getFilesScrollEl();

            return {
                path: this.lastCurrentPath,
                expandedKeys: expandedKeys,
                dirsScrollTop: left ? left.scrollTop : 0,
                filesScrollTop: right ? right.scrollTop : 0,
            };
        },
        applyScroll(state, options) {
            let skipFiles = options && options.skipFiles;
            [0, 250, 600].forEach((delay) => {
                setTimeout(() => {
                    let left = this.getDirsScrollEl();
                    if (left) left.scrollTop = state.dirsScrollTop;
                    if (!skipFiles) {
                        let right = this.getFilesScrollEl();
                        if (right) right.scrollTop = state.filesScrollTop;
                    }
                }, delay);
            });
        },
        reExpandDirs(state) {
            let tree = this.getDirsTreeView();
            if (tree && state.expandedKeys) {
                state.expandedKeys.forEach((key) => {
                    try {
                        tree.expandItem(key);
                    } catch (e) {
                        console.error(e);
                    }
                });
            }
        },
        restoreLeftState(state) {
            if (!state) return;

            this.$nextTick(() => {
                this.reExpandDirs(state);
                this.applyScroll(state, {skipFiles: true});
            });
        },
        restoreUiState(state) {
            if (!state) return;

            if (state.path) {
                this.fileManager.option('currentPath', state.path);
            }

            this.$nextTick(() => {
                this.reExpandDirs(state);
                this.applyScroll(state);
            });
        }
    },
    watch: {
        items: function () {
            if (this.$refs.fileManager && this.fileManager) {
                let state = this.captureUiState();
                let navigatePath = this.pendingNavigatePath;
                this.pendingNavigatePath = null;

                setTimeout(() => {
                    let result = this.fileManager.refresh();

                    let finalize = () => {
                        if (navigatePath !== null && navigatePath !== undefined) {
                            this.fileManager.option('currentPath', navigatePath);
                            this.restoreLeftState(state);
                        } else {
                            this.restoreUiState(state);
                        }
                    };

                    if (result && typeof result.done === 'function') {
                        result.done(finalize);
                    } else if (result && typeof result.then === 'function') {
                        result.then(finalize);
                    } else {
                        finalize();
                    }
                }, 500);
            }
        },
    }
}
</script>

<style>
</style>